import cloneDeep from 'lodash/cloneDeep';
import type { CompleteType } from '../util/general';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import { getDefaultGroupId } from './tenantModel';
import { itemAccessControlLevels, type ItemAccessControl } from './itemAccessControlDto';

export const isInstanceOfItemAccessControlDto = (obj: unknown): obj is ItemAccessControl => {
  if (!isObject(obj)) return false;
  const possibleDto = obj as Partial<ItemAccessControl>;
  return (typeof possibleDto.level === 'string' && itemAccessControlLevels.includes(possibleDto.level)) || Array.isArray(possibleDto.groups);
};

export const toAccessControlDto = (access: ItemAccessControl): CompleteType<ItemAccessControl> => ({
  level: access.level,
  groups: access.groups,
});

/**
 * Returns default visibleTo access control for items.
 *
 * Priority:
 * 1. If primaryGroupDefaultVisibleTo is provided, use it (admin-configured per-group default)
 * 2. Otherwise, fall back to existing logic:
 *    - For existing items or when primary group is the tenant default group, return tenant-wide visibility
 *    - Otherwise, return visibility restricted to user's primary group
 *
 * @param tenantId Tenant ID of user making the request
 * @param primaryGroupId Primary group ID of the user making the request
 * @param forExistingItem Whether this request is for an existing item. This is required for backwards
 * compatibility purposes since previously, the default visibility of items used to be everyone in the tenant.
 * @param primaryGroupDefaultVisibleTo Admin-configured default visibility for the user's primary group
 */
export const getDefaultVisibleTo = (
  tenantId: string,
  primaryGroupId: string,
  forExistingItem: boolean,
  primaryGroupDefaultVisibleTo: ItemAccessControl | undefined | null,
): ItemAccessControl => {
  if (primaryGroupDefaultVisibleTo) {
    return primaryGroupDefaultVisibleTo;
  }
  const defaultGroupId = getDefaultGroupId(tenantId);
  if (forExistingItem || defaultGroupId === primaryGroupId) {
    return getDefaultAccessControl();
  }
  return {
    level: 'selected_groups',
    groups: [primaryGroupId],
  };
};

export const getDefaultAccessControl = (): ItemAccessControl => ({
  level: 'tenant',
  groups: [],
});

/**
 * Returns default updatableBy access control for items.
 *
 * Priority:
 * 1. If primaryGroupDefaultUpdatableBy is provided, use it (admin-configured per-group default)
 * 2. Otherwise, return undefined to preserve existing behavior where items without updatableBy
 *    are editable by everyone in the organization group plus the owner
 *
 * @param primaryGroupDefaultUpdatableBy Optional admin-configured default updatableBy for the user's primary group
 */
export const getDefaultUpdatableBy = (
  primaryGroupDefaultUpdatableBy?: ItemAccessControl | null,
): ItemAccessControl | undefined => {
  if (primaryGroupDefaultUpdatableBy) {
    return primaryGroupDefaultUpdatableBy;
  }

  return undefined;
};

export const getSanitizedAccessControlForIngest = (access: ItemAccessControl): { sanitizedAccess: ItemAccessControl | undefined, errorMsg?: string } => {
  const sanitizedAccess = cloneDeep(access);

  if (access.level === 'tenant') {
    return { sanitizedAccess: toAccessControlDto(access) };
  }

  if (sanitizedAccess.level === 'owner') {
    return { sanitizedAccess: undefined, errorMsg: `Cannot set access control level to ${sanitizedAccess.level}` };
  }

  return { sanitizedAccess: toAccessControlDto(sanitizedAccess) };
};
